/**
 * Hash a password using Web Crypto API (browser-compatible)
 * Used for sending hashed password to server
 */
export const hashPassword = async (password: string): Promise<string> => {
  const salt = 'stride_weight_tracker_salt_2025';
  const textEncoder = new TextEncoder();
  const data = textEncoder.encode(password + salt);

  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));
  const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

  return hashHex;
};

/**
 * Generate a secure session token using Web Crypto API
 */
export const generateSessionToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Verify if user is authenticated
 */
export const isAuthenticated = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const token = localStorage.getItem('stride_auth_token');
  const timestamp = localStorage.getItem('stride_auth_timestamp');
  
  if (!token || !timestamp) return false;
  
  // Check if token is expired (24 hours)
  const now = Date.now();
  const authTime = parseInt(timestamp, 10);
  const expirationTime = 24 * 60 * 60 * 1000; // 24 hours
  
  if (now - authTime > expirationTime) {
    localStorage.removeItem('stride_auth_token');
    localStorage.removeItem('stride_auth_timestamp');
    localStorage.removeItem('stride_authenticated');
    return false;
  }
  
  return true;
};

/**
 * Set authentication token
 */
export const setAuthToken = (signedToken?: string): void => {
  if (signedToken) {
    // Use the signed token from server
    localStorage.setItem('stride_auth_token', signedToken);
  } else {
    // Generate local token for development
    const token = generateSessionToken();
    localStorage.setItem('stride_auth_token', token);
  }
  
  const timestamp = Date.now().toString();
  localStorage.setItem('stride_auth_timestamp', timestamp);
  localStorage.setItem('stride_authenticated', 'true');
};

/**
 * Clear authentication
 */
export const clearAuth = (): void => {
  localStorage.removeItem('stride_auth_token');
  localStorage.removeItem('stride_auth_timestamp');
  localStorage.removeItem('stride_authenticated');
};

/**
 * Get the current session token for API requests
 */
export const getSessionToken = (): string | null => {
  if (!isAuthenticated()) return null;
  return localStorage.getItem('stride_auth_token');
};

